import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { z } from "zod";
import { glimpseAPI } from "./api";
import { logout } from "../auth/";
import { createMiddleware } from "../middlewareUtil";
import FrameSizes from "../../components/glimpse/FrameSizes";

// Initial state (based on the original reducer)
const initialState = {
  adaptMethod: 1,
  modelClipSnapshot: undefined,
  modelCaseId: undefined,
  modelClipId: undefined,
  modelAudioDuration: undefined,
  frameSizeLow: 2048,
  frameSizeHi: 4096,
  useFrameSizeList: false,
  frameSizeListMin: 2048,
  frameSizeListMax: 4096,
  frameSizeListSteps: 5,
  speedOfSoundInCm: 34300,
  gateLevelGF: 95,
  midModelLengthGF: 2.0,
  utteranceLength: 50,
  gateLevelD: 50,
  midModelLengthD: 5.0,
  lowPass: undefined,
  highPass: 150,
  arrayWidth: 40,
  modelSampleRate: undefined,
  valid: true,
  validationErrors: {
    fieldErrors: {},
    crossFieldErrors: [],
  },

  // Loading states
  settingModel: false,
  queueing: false,
};

const glimpseParametersSchema = z
  .object({
    frameSizeLow: z.coerce.number().int().min(128).max(262144),
    frameSizeHi: z.coerce.number().int().min(128).max(262144),
    useFrameSizeList: z.boolean(),
    frameSizeListMin: z.coerce
      .number()
      .int()
      .min(FrameSizes[0])
      .max(FrameSizes[FrameSizes.length - 1]),
    frameSizeListMax: z.coerce
      .number()
      .int()
      .min(FrameSizes[0])
      .max(FrameSizes[FrameSizes.length - 1]),
    frameSizeListSteps: z.coerce
      .number()
      .int()
      .min(2, "Must be at least 2 steps")
      .max(10, "Must be less than or equal to 10 steps"),
    speedOfSoundInCm: z.coerce
      .number()
      .int()
      .min(1000, "Must be at least 1000cm/s"),
    gateLevelGF: z.coerce
      .number()
      .int()
      .min(1, "Must be at least 1%")
      .max(100, "Must be less than or equal to 100%"),
    midModelLengthGF: z.coerce
      .number()
      .min(0.1, "Must be at least 1% of modeling data"),
    utteranceLength: z.coerce
      .number()
      .int()
      .min(1, "Must be at least 1ms")
      .max(1000, "Must be less than or equal to 1000ms"),
    gateLevelD: z.coerce
      .number()
      .int()
      .min(1, "Must be at least 1%")
      .max(100, "Must be less than or equal to 100%"),
    midModelLengthD: z.coerce
      .number()
      .min(0.1, "Must be at least 1% of modeling data"),
    lowPass: z.coerce
      .number()
      .int()
      .min(100, "Must be at least 100hz")
      .max(44100, "Must be lower than 44.1Khz")
      .optional(),
    highPass: z.coerce
      .number()
      .int()
      .min(100, "Must be at least 100hz")
      .max(44100, "Must be lower than 44.1Khz"),
    arrayWidth: z.coerce.number().int().min(1, "Must be at least 1cm"),
  })
  .refine(
    (data) => {
      // High pass must be less than low pass (if low pass is defined)
      if (data.lowPass && data.highPass) {
        return data.highPass < data.lowPass;
      }
      return true;
    },
    {
      message: "High pass frequency must be less than low pass frequency",
      path: ["crossField"],
    },
  )
  .refine(
    (data) => {
      // Frame size high should be >= frame size low
      return data.frameSizeHi >= data.frameSizeLow;
    },
    {
      message:
        "High frame size must be greater than or equal to low frame size",
      path: ["crossField"],
    },
  );

const crossFieldErrorMapping = {
  "High pass frequency must be less than low pass frequency": [
    "highPass",
    "lowPass",
  ],
  "High frame size must be greater than or equal to low frame size": [
    "frameSizeLow",
    "frameSizeHi",
  ],
};

const validateParams = (params) => {
  if (!params) {
    return {
      isValid: false,
      fieldErrors: {},
      crossFieldErrors: ["Parameters object is required"],
    };
  }

  try {
    glimpseParametersSchema.parse(params);

    return {
      isValid: true,
      fieldErrors: {},
      crossFieldErrors: [],
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldErrors = {};
      const crossFieldErrors = [];

      error.issues.forEach((err) => {
        if (err.path.includes("crossField")) {
          const { message } = err;
          crossFieldErrors.push(message);
          const affectedFields = crossFieldErrorMapping[message];

          if (affectedFields) {
            // Map the cross-field error to affected fields
            affectedFields.forEach((fieldName) => {
              if (!fieldErrors[fieldName]) {
                fieldErrors[fieldName] = [];
              }
              fieldErrors[fieldName].push(message);
            });
          }
        } else if (err.path.length > 0) {
          const fieldName = err.path[0];
          if (!fieldErrors[fieldName]) {
            fieldErrors[fieldName] = [];
          }
          fieldErrors[fieldName].push(err.message);
        }
      });

      return {
        isValid: false,
        fieldErrors,
        crossFieldErrors,
      };
    }

    // Fallback for non-Zod errors
    return {
      isValid: false,
      fieldErrors: {},
      crossFieldErrors: ["Unknown validation error"],
    };
  }
};

const getFieldRules = (fieldName) => {
  const shape = glimpseParametersSchema.shape;
  const fieldSchema = shape[fieldName];

  if (!fieldSchema) return {};

  const rules = {
    required: !fieldSchema.isOptional(),
    type: "unknown",
  };

  if (fieldSchema._def) {
    const def = fieldSchema._def;
    if (def.typeName === "ZodNumber" || def.typeName === "ZodPipeline") {
      rules.type = "number";
    }
  }

  return rules;
};

export const setModel = createAsyncThunk(
  "glimpse/setModel",
  async (
    { modelCaseId, modelClipId },
    { getState, dispatch, rejectWithValue },
  ) => {
    const accessToken = getState().auth.accessCode;

    const result = await glimpseAPI.getModelClip(
      accessToken,
      modelCaseId,
      modelClipId,
    );

    if (result.success) {
      const clipSnapshot = result.data;

      // Calculate derived parameters (from original logic)
      const sampleRate = clipSnapshot.audioFiles[0].sampleRate;
      const fileSize = clipSnapshot.audioFiles[0].fileSize;
      const audioDuration = (fileSize - 44) / 4 / sampleRate; // TODO: Add WAV header size to audio data.

      if (audioDuration && audioDuration > 0.001) {
        let newMM = audioDuration * 0.95; // Issue #233
        if (isNaN(newMM) || newMM < 0) newMM = 0;
        newMM = newMM.toFixed(2);

        let lowPass = Math.min(7550, sampleRate / 2 - 50);

        return {
          modelAudioDuration: audioDuration,
          modelCaseId: modelCaseId,
          modelClipId: modelClipId,
          modelClipSnapshot: clipSnapshot,
          midModelLengthGF: newMM,
          lowPass: lowPass.toFixed(0),
          midModelLengthD: newMM,
          modelSampleRate: sampleRate,
        };
      }

      return rejectWithValue("Invalid audio duration");
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to load model clip");
  },
);

export const queueRequest = createAsyncThunk(
  "glimpse/queueRequest",
  async (
    { modelCaseId, modelClipId, sourceCaseId, sourceClipId, parameters },
    { getState, dispatch, rejectWithValue },
  ) => {
    const accessToken = getState().auth.accessCode;

    const glimpseRequest = {
      modelCaseId,
      modelClipId,
      sourceCaseId,
      sourceClipId,
      ...parameters,
    };

    const result = await glimpseAPI.queueRequest(
      accessToken,
      sourceCaseId,
      sourceClipId,
      glimpseRequest,
    );

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to queue glimpse request");
  },
);

// Create the slice
const glimpseSlice = createSlice({
  name: "glimpse",
  initialState,
  reducers: {
    reset: () => cloneDeep(initialState),
    resetParameters: (state) => {
      const newState = cloneDeep(initialState);
      let newMM = state.modelAudioDuration * 0.95;
      if (isNaN(newMM) || newMM < 0) newMM = 0;
      newMM = newMM.toFixed(2);
      newState.modelCaseId = state.modelCaseId;
      newState.modelClipId = state.modelClipId;
      newState.modelClipSnapshot = state.modelClipSnapshot;
      newState.modelAudioDuration = state.modelAudioDuration;
      newState.midModelLengthGF = newMM;
      newState.lowPass = Math.min(7550, state.modelSampleRate / 2 - 50).toFixed(
        0,
      );
      newState.midModelLengthD = newMM;
      newState.modelSampleRate = state.modelSampleRate;
      return newState;
    },

    update: (state, action) => {
      // Apply the update
      if (
        action.payload.frameSizeLow &&
        Number(action.payload.frameSizeLow) > Number(state.frameSizeHi)
      ) {
        action.payload.frameSizeHi = action.payload.frameSizeLow;
      }
      if (
        action.payload.frameSizeHi &&
        Number(action.payload.frameSizeHi) < Number(state.frameSizeLow)
      ) {
        action.payload.frameSizeLow = action.payload.frameSizeHi;
      }
      Object.assign(state, action.payload);

      // Validate the updated parameters
      const validationResult = validateParams(state);

      // Update validation state
      state.valid = validationResult.isValid;
      state.validationErrors = {
        fieldErrors: validationResult.fieldErrors,
        crossFieldErrors: validationResult.crossFieldErrors,
      };
    },

    setDefaults: (state, action) => {
      Object.assign(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Set model
      .addCase(setModel.pending, (state) => {
        state.settingModel = true;
      })
      .addCase(setModel.fulfilled, (state, action) => {
        state.settingModel = false;
        Object.assign(state, action.payload);
      })
      .addCase(setModel.rejected, (state) => {
        state.settingModel = false;
        // Reset state on error (as per original implementation)
        return cloneDeep(initialState);
      })

      // Queue request
      .addCase(queueRequest.pending, (state) => {
        state.queueing = true;
      })
      .addCase(queueRequest.fulfilled, (state) => {
        state.queueing = false;
      })
      .addCase(queueRequest.rejected, (state) => {
        state.queueing = false;
      });
  },
});

export const glimpseMiddleware = createMiddleware([
  "glimpse/queueRequest/pending",
]);

export const { reset, update, setDefaults, resetParameters } =
  glimpseSlice.actions;

export { glimpseParametersSchema, getFieldRules, validateParams };

export default glimpseSlice.reducer;
